import { merge, sortedIndexBy } from 'lodash/fp'
import { Event } from '@wellesley/openapi'
import { EventConfigs, MyEventsFilters } from '../helpers/Event.types'

export type EventsSliceState = {
  configs: EventConfigs
  items: Event[]
  filters: MyEventsFilters | null
  enableNextPageFetch: boolean
}

export function getEventIndex(events: Event[], eventId: string) {
  return events.findIndex((evt) => evt.id === eventId)
}

// startTime ASC, then id ASC
function compareEventsAsc(a: Event, b: Event) {
  if (a.startTime < b.startTime) return -1
  if (a.startTime > b.startTime) return 1

  if (a.id < b.id) return -1
  if (a.id > b.id) return 1
  return 0
}

const eventSortKeyAsc = (e: Event) => `${e.startTime}|${e.id}`
function findInsertIndexAsc(items: Event[], target: Event) {
  return sortedIndexBy(eventSortKeyAsc, target, items)
}

/**
 * If the list is empty, the event is simply added.
 * If the current page is full and the event belongs to a later page, the event is not inserted and next page should be fetched.
 * If the event should be within the currently loaded range, it's inserted at the correct position.
 * If inserting the new event causes the page to overflow by 1 item, the last item is popped to maintain page size.
 */
export function handleEventAdd(
  state: EventsSliceState,
  event: Event,
  pageSize: number,
): void {
  const eventIdx = getEventIndex(state.items, event.id)

  // Event already exists, do nothing
  if (eventIdx !== -1) return

  // If list is empty, just add
  if (state.items.length === 0) {
    state.items.push(event)
    return
  }

  const lastEvent = state.items.at(-1)!
  const isAfterLast = compareEventsAsc(event, lastEvent) > 0

  // If page is full and event is later than last item: skip and mark to fetch next
  if (state.items.length % pageSize === 0 && isAfterLast) {
    state.enableNextPageFetch = true
    return
  }

  // Insert into right position
  const insertIndex = findInsertIndexAsc(state.items, event)
  state.items.splice(insertIndex, 0, event)

  // If page size overflows remove last item
  if ((state.items.length - 1) % pageSize === 0) {
    state.items.pop()
    state.enableNextPageFetch = true
  }
}

/**
 * If event is not found in the list, does nothing.
 * If new `startTime` is after the last loaded event and page is full, removes the event and sets `enableNextPageFetch = true`.
 * If `startTime` changes and new position is within range, repositions it.
 * If `startTime` hasn't changed, updates the event in place.
 */
export function handleEventUpdate(
  state: EventsSliceState,
  args: {
    id: string
    patch: Event | Partial<Event>
    override?: boolean
  },
  pageSize: number,
): void {
  const { id, patch, override } = args

  const index = getEventIndex(state.items, id)
  if (index === -1) return

  const originalLength = state.items.length
  const prevEvent = state.items[index]
  const updatedEvent = override ? (patch as Event) : merge(prevEvent, patch)

  if (originalLength === 1) {
    state.items[index] = updatedEvent
    return
  }

  const startTimeChanged = prevEvent.startTime !== updatedEvent.startTime

  // If startTime hasn't changed, update in place to preserve order
  if (!startTimeChanged) {
    state.items[index] = updatedEvent
    return
  }

  // Remove the event
  state.items.splice(index, 1)

  // If new position is beyond range, do not re-insert
  const lastEvent = state.items.at(-1)!
  const isAfterLast = compareEventsAsc(updatedEvent, lastEvent) > 0

  if (isAfterLast && originalLength % pageSize === 0) {
    state.enableNextPageFetch = true
    return
  }

  // Re-insert at correct position
  const insertIndex = findInsertIndexAsc(state.items, updatedEvent)
  state.items.splice(insertIndex, 0, updatedEvent)
}
